import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import { useModelStore } from "../../../store/useModelStore";
import { useBeamSectionStore } from "../../../hooks/useBeamSection";
import { useBeamIntersection } from "@/hooks/useBeamIntersection";
import { IntersectionDialog } from "@/components/ui/IntersectionDialog";
import { PerpPreviewLine } from "./PerpPreviewLine";

/**
 * BeamElement Component
 * 
 * TRANSPARENCY BEHAVIOR (Section-Beam Visibility):
 * ================================================
 * This component implements automatic beam transparency based on section assignment:
 * 
 * 1. BEAM WITH SECTION (Section Visible):
 *    - Beam: HIDDEN (opacity: 0.0) - Completely invisible
 *    - Section: Fully Visible - Shows actual cross-section geometry
 *    → User sees ONLY the detailed section mesh, beam is hidden
 * 
 * 2. BEAM WITH SECTION (Section Hidden):
 *    - Beam: Fully Visible (opacity: 1.0) - Normal view
 *    - Section: Hidden
 *    → Section visibility toggle hides sections, beams remain visible
 * 
 * 3. BEAM WITHOUT SECTION:
 *    - Beam: Fully Visible (opacity: 1.0) - Normal view
 *    - Section: N/A
 *    → Standard beam rendering without any section
 * 
 * 4. SECTION REMOVED:
 *    - Beam: Fully Visible (opacity: 1.0) - Returns to normal
 *    - Section: N/A
 *    → Removing section restores full beam visibility
 * 
 * LOGIC: opacity = (hasSection && isSectionVisible) ? 0.0 : 1.0
 * 
 * This ensures only one visualization is dominant:
 * - Either the SECTION (when applied and visible) - beam is HIDDEN
 * - Or the BEAM (when no section or section hidden) - beam is VISIBLE
 */

// BeamCenterIndicator component to show center point
const BeamCenterIndicator: React.FC<{
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  visible: boolean;
}> = ({ startPoint, endPoint, visible }) => {
  const centerPoint = useMemo(() => {
    return new THREE.Vector3(
      (startPoint.x + endPoint.x) / 2,
      (startPoint.y + endPoint.y) / 2,
      (startPoint.z + endPoint.z) / 2
    );
  }, [startPoint, endPoint]);

  if (!visible) return null;

  return (
    <mesh position={centerPoint}>
      <sphereGeometry args={[0.15]} />
      <meshStandardMaterial color="#4dabf7" transparent opacity={0.8} />
    </mesh>
  );
};

interface BeamElementProps {
  beamId: string;
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  isSelected?: boolean;
  material?: string;
  onBeamClick?: (beamId: string, clickPosition?: THREE.Vector3) => void;
  onBeamHover?: (beamId: string | null) => void;
  onBeamRightClick?: (beamId: string, event?: any) => void;
  onBeamDoubleClick?: (beamId: string, event?: any) => void;
  showArrow?: boolean;
  highlightColor?: string;
  startNode?: { x: number; y: number; z: number };
  endNode?: { x: number; y: number; z: number };
  isNodeModeActive?: boolean; // Only show mid-point indicator when actively creating nodes (not just panel expanded)
  isDivideBeamMode?: boolean;
}

// Arrow indicator component for beam midpoint
const BeamArrowIndicator: React.FC<{
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  visible: boolean;
  onArrowClick?: () => void;
}> = ({ startPoint, endPoint, visible, onArrowClick }) => {
  const midpoint = useMemo(() => {
    return new THREE.Vector3(
      (startPoint.x + endPoint.x) / 2,
      (startPoint.y + endPoint.y) / 2,
      (startPoint.z + endPoint.z) / 2
    );
  }, [startPoint, endPoint]);

  if (!visible) return null;

  return (
    <group
      position={midpoint}
      onClick={(e) => {
        e.stopPropagation();
        onArrowClick?.();
      }}
    >
      {/* Arrow mesh */}
      <mesh>
        <sphereGeometry args={[0.2]} />
        <meshStandardMaterial
          color="#FF6B35"
          emissive="#FF6B35"
          emissiveIntensity={0.5}
          transparent
          opacity={0.9}
        />
      </mesh>
    </group>
  );
};

import { useThree } from "@react-three/fiber";

export const BeamElement: React.FC<BeamElementProps> = ({
  beamId,
  startPoint,
  endPoint,
  isSelected = false,
  material = "steel",
  onBeamClick,
  onBeamHover,
  onBeamRightClick,
  onBeamDoubleClick,
  showArrow = false,
  highlightColor,
  startNode,
  endNode,
  isNodeModeActive = false,
  isDivideBeamMode = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { setHoveredBeam, divideBeamAtMidpoint } = useModelStore();
  const { camera, gl } = useThree();
  const {
    showDialog,
    intersectionPoint,
    handleBeamIntersection,
    handleIntersectionConfirm,
    handleIntersectionCancel,
  } = useBeamIntersection(camera, gl.domElement);

  // Check if this beam has a section applied
  const hasSection = useBeamSectionStore((state) => !!state.beamSectionMap[beamId]);
  const isSectionVisible = useBeamSectionStore((state) => state.isVisible);

  // Debug logging for section status changes
  React.useEffect(() => {
    if (hasSection) {
      console.log(`🎨 Beam ${beamId}: Section applied, visibility=${isSectionVisible}, opacity will be ${isSectionVisible ? '0.0' : '1.0'}`);
    }
  }, [beamId, hasSection, isSectionVisible]);

  // Create points for the beam - use exact coordinates for perfect straightness
  const points = [
    new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z),
    new THREE.Vector3(endPoint.x, endPoint.y, endPoint.z),
  ];

  // Enhanced material properties with consistent colors
  const color = highlightColor
    ? highlightColor
    : isSelected
      ? "#ff6b6b"
      : isHovered
        ? "#4a9eff"
        : "#4dabf7";
  const lineWidth = isSelected ? 4 : isHovered ? 3.5 : 3;
  
  // Hide beam completely when section is applied and visible
  // Logic: If beam has section AND sections are visible → beam is HIDDEN (opacity: 0)
  //        Otherwise → beam is fully visible (1.0)
  const opacity = (hasSection && isSectionVisible) ? 0.0 : 1.0;

  // Track selection state
  React.useEffect(() => {
    if (isSelected) {
      //console.log('🎯 Beam selected:', beamId);
    }
  }, [isSelected, beamId]);

  const handlePointerEnter = (e: any) => {
    e.stopPropagation();
    //console.log('🎯 Beam hovered:', beamId);
    setIsHovered(true);
    setHoveredBeam(beamId);
    onBeamHover?.(beamId);
    document.body.style.cursor = "pointer";
  };

  const handlePointerLeave = (e: any) => {
    e.stopPropagation();
    setIsHovered(false);
    setHoveredBeam(null);
    onBeamHover?.(null);
    document.body.style.cursor = "default";
  };

  // Around line 174 - Update the existing function:
  const handleArrowClick = () => {
    // Only allow division if divide mode is active
    if (!isDivideBeamMode) {
      console.warn("⚠️ Divide beam mode not active");
      return;
    }

    const result = divideBeamAtMidpoint(beamId);
    if (result) {
      console.log("✅ Beam divided successfully!");
    } else {
      console.log("❌ Failed to divide beam");
    }
  };

  useEffect(() => {
    // Check for intersections when the beam is first created
    if (beamId && startPoint && endPoint) {
      const beam = {
        id: beamId,
        startNodeId: startPoint.toString(),
        endNodeId: endPoint.toString(),
        material: material as "steel",
        section: "default",
        localAxes: {
          x: [1, 0, 0] as [number, number, number],
          y: [0, 1, 0] as [number, number, number],
          z: [0, 0, 1] as [number, number, number],
        },
        betaAngle: 0,
        releases: {
          start: {
            fx: false,
            fy: false,
            fz: false,
            mx: false,
            my: false,
            mz: false,
          },
          end: {
            fx: false,
            fy: false,
            fz: false,
            mx: false,
            my: false,
            mz: false,
          },
        },
        isSelected: false,
        isHovered: false,
      };
      handleBeamIntersection(beam);
    }
  }, [beamId, startPoint, endPoint, material, handleBeamIntersection]);

  // Get prep mode state
  const isPrepMode = useModelStore((state) => state.isPrepMode);
  const referenceBeamId = useModelStore((state) => state.referenceBeamId);

  return (
    <group>
      {/* Main beam line - perfectly straight, HIDDEN when section is applied */}
      <Line
        points={points}
        color={color}
        lineWidth={lineWidth}
        transparent={true}
        opacity={opacity}
        onClick={(e) => {
          e.stopPropagation();
          const clickPosition = e.point
            ? new THREE.Vector3(e.point.x, e.point.y, e.point.z)
            : undefined;
          onBeamClick?.(beamId, clickPosition);
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          onBeamRightClick?.(beamId, e);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onBeamDoubleClick?.(beamId, e);
        }}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      />

      {/* Center point indicator - only visible when actively creating nodes (user clicked to enter node creation mode) */}
      <BeamCenterIndicator
        startPoint={startPoint}
        endPoint={endPoint}
        visible={isHovered && isNodeModeActive && !isDivideBeamMode}
      />

      {/* Beam center indicator arrow */}
      <BeamArrowIndicator
        startPoint={startPoint}
        endPoint={endPoint}
        visible={isHovered && isDivideBeamMode}
        onArrowClick={handleArrowClick}
      />

      {/* Perpendicular preview lines in prep mode */}
      {isPrepMode && referenceBeamId === beamId && startNode && (
        <PerpPreviewLine
          startPoint={startNode}
          referenceBeam={{ startPoint, endPoint }}
        />
      )}

      {isPrepMode && referenceBeamId === beamId && endNode && (
        <PerpPreviewLine
          startPoint={endNode}
          referenceBeam={{ startPoint, endPoint }}
        />
      )}

      {/* Intersection dialog */}
      {showDialog && intersectionPoint && (
        <IntersectionDialog
          position={
            new THREE.Vector3(
              intersectionPoint.point.x,
              intersectionPoint.point.y,
              intersectionPoint.point.z
            )
          }
          visible={showDialog}
          onYes={handleIntersectionConfirm}
          onNo={handleIntersectionCancel}
        />
      )}
    </group>
  );
};
